// src/components/common/RoleProtectedRoute.js
import React from 'react';
import { Navigate } from 'react-router-dom';
import { getCurrentUser } from '../../services/auth';
import {
  canonicalizeUserRole,
  isGlobalAdmin,
  isTenantScopedAdmin,
  isVirtualMedicInstructor,
  isUniversityAdmin,
  isUniversityInstructor,
} from '../../utils/role';
import { Container, Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';

function RoleProtectedRoute({
  children,
  allowedRoles = [],
  allowedAccountTypes = [],
  fallbackPath = '/dashboard',
}) {
  const user = getCurrentUser();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (
    Array.isArray(allowedAccountTypes)
    && allowedAccountTypes.length > 0
    && !allowedAccountTypes.includes(user.accountType)
  ) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="warning">
          <AlertTitle>Account type mismatch</AlertTitle>
          <Stack spacing={2}>
            <Typography variant="body2">
              This page is not available for your account type. Please return to the dashboard.
            </Typography>
            <Button variant="contained" href={fallbackPath}>
              Back to dashboard
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }

  const expandAllowedRoles = (roles = []) => {
    const map = new Map([
      ['admin', ['global_admin', 'university_admin']],
      ['instructor', ['university_instructor', 'vm_instructor']],
      ['tenant_staff', ['university_admin', 'university_instructor']],
      ['tenant-admin', ['university_admin']],
      ['tenant-instructor', ['university_instructor']],
      ['global_staff', ['global_admin', 'vm_instructor']],
    ]);

    const expanded = new Set();
    (Array.isArray(roles) ? roles : [roles]).forEach((roleKey) => {
      const normalized = (roleKey || '').toString().toLowerCase();
      if (!normalized) {
        return;
      }
      if (map.has(normalized)) {
        map.get(normalized).forEach((entry) => expanded.add(entry));
      } else {
        expanded.add(normalized);
      }
    });

    return expanded;
  };

  const hasRequiredRole = (userObj, roles = []) => {
    if (!Array.isArray(roles) || roles.length === 0) {
      return true;
    }

    if (isGlobalAdmin(userObj)) {
      return true;
    }

    const allowed = expandAllowedRoles(roles);
    const canonicalRole = canonicalizeUserRole(userObj);

    if (allowed.has(canonicalRole)) {
      return true;
    }

    if (allowed.has('tenant_staff') && isTenantScopedAdmin(userObj)) {
      return true;
    }

    if (allowed.has('university_admin') && isUniversityAdmin(userObj)) {
      return true;
    }

    if (allowed.has('university_instructor') && isUniversityInstructor(userObj)) {
      return true;
    }

    if (allowed.has('vm_instructor') && isVirtualMedicInstructor(userObj)) {
      return true;
    }

    if (allowed.has('student') && ['student', 'doctor', 'educator'].includes(canonicalRole)) {
      return true;
    }

    return false;
  };

  if (!hasRequiredRole(user, allowedRoles)) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          <AlertTitle>Access Denied</AlertTitle>
          You don't have permission to access this page. This section is restricted to {allowedRoles.join(', ')} users only.
        </Alert>
      </Container>
    );
  }
  
  return children;
}

export default RoleProtectedRoute;
